import { SUPABASE_URL } from '../../utils/supabase/url';
"use client";

import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { getAuthHeaders } from "../../utils/supabase/client";
import { projectId } from "../../utils/supabase/info";
import {
  logEdgeTransportIssueOnce,
  markEdgeBackendAvailable,
  markEdgeBackendUnavailable,
  shouldAttemptEdgeRequest,
} from "../../utils/edgeNetworkState";

import { cn } from "./utils";
import { invalidateStoredAvatarAccessUrl, isStoredAvatarId, resolveStoredAvatarAccessUrl } from './avatarAccessLinks';

type CachedAvatarAccessLink = {
  accessUrl: string;
  expiresAt: number;
};

type CachedAvatarFailure = {
  expiresAt: number;
};

const avatarAccessLinkCache = new Map<string, CachedAvatarAccessLink>();
const avatarAccessLinkInflight = new Map<string, Promise<string>>();
const avatarAccessLinkFailureCache = new Map<string, CachedAvatarFailure>();

export function parseDriveAvatarFileId(src?: string | null): string | null {
  if (!src || typeof src !== "string") return null;
  if (!src.startsWith("drive-file:")) return null;
  const fileId = src.slice("drive-file:".length).trim();
  return fileId || null;
}

/** Drop a cached access URL. The link carries a 10-minute expiry, but a URL
 *  can stop working before that (revision rolled, grant revoked, clock skew).
 *  Without this the avatar renders once, errors, and keeps failing for the
 *  rest of the TTL because every re-render is handed the same dead URL. */
export function invalidateDriveAvatarAccessUrl(fileId: string): void {
  invalidateStoredAvatarAccessUrl(fileId);
  avatarAccessLinkCache.delete(fileId);
  avatarAccessLinkInflight.delete(fileId);
}

export async function resolveDriveAvatarAccessUrl(fileId: string): Promise<string> {
  if (isStoredAvatarId(fileId)) return resolveStoredAvatarAccessUrl(fileId);
  const cached = avatarAccessLinkCache.get(fileId);
  if (cached && cached.expiresAt > Date.now() + 30_000) {
    return cached.accessUrl;
  }

  const failed = avatarAccessLinkFailureCache.get(fileId);
  if (failed && failed.expiresAt > Date.now()) {
    throw new Error('Avatar access link temporarily unavailable');
  }

  const inflight = avatarAccessLinkInflight.get(fileId);
  if (inflight) {
    return inflight;
  }

  const request = (async () => {
    const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/files/access-link/${fileId}`;
    if (!shouldAttemptEdgeRequest(url)) {
      avatarAccessLinkFailureCache.set(fileId, { expiresAt: Date.now() + 60_000 });
      throw new Error('Avatar access link temporarily unavailable');
    }

    const response = await fetch(
      url,
      {
        method: "POST",
        headers: await getAuthHeaders({ "Content-Type": "application/json" }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to resolve avatar access link: ${response.status}`);
    }

    const data = await response.json();
    markEdgeBackendAvailable();
    avatarAccessLinkFailureCache.delete(fileId);
    const expiresAt = data?.expiresAt ? Date.parse(data.expiresAt) : Date.now() + 9 * 60 * 1000;
    avatarAccessLinkCache.set(fileId, {
      accessUrl: data.accessUrl,
      expiresAt,
    });
    return data.accessUrl as string;
  })();

  avatarAccessLinkInflight.set(fileId, request);

  try {
    return await request;
  } catch (error) {
    avatarAccessLinkFailureCache.set(fileId, { expiresAt: Date.now() + 60_000 });
    markEdgeBackendUnavailable(error);
    throw error;
  } finally {
    avatarAccessLinkInflight.delete(fileId);
  }
}

export async function resolveAvatarSourceUrl(src?: string | null): Promise<string | null> {
  if (!src || typeof src !== "string") {
    return null;
  }

  const trimmed = src.trim();
  if (!trimmed) {
    return null;
  }

  const driveFileId = parseDriveAvatarFileId(trimmed);
  if (!driveFileId) {
    return trimmed;
  }

  return resolveDriveAvatarAccessUrl(driveFileId);
}

const AvatarModule = AvatarPrimitive as any;
const RadixAvatar = Object.assign(
  {},
  AvatarModule,
  typeof AvatarModule?.default === "object" ? AvatarModule.default : {},
);
RadixAvatar.Root = AvatarModule?.Root ?? AvatarModule?.default ?? AvatarModule;
RadixAvatar.Image = AvatarModule?.Image ?? AvatarModule?.default?.Image;
RadixAvatar.Fallback = AvatarModule?.Fallback ?? AvatarModule?.default?.Fallback;

const hasRadixAvatar =
  typeof RadixAvatar?.Root !== "undefined" &&
  typeof RadixAvatar?.Image !== "undefined" &&
  typeof RadixAvatar?.Fallback !== "undefined";

let hasWarnedMissingRadixAvatar = false;
const warnMissingRadixAvatar = () => {
  if (hasWarnedMissingRadixAvatar) return;
  hasWarnedMissingRadixAvatar = true;
  console.warn(
    "[ui/avatar] Radix Avatar primitives unavailable. Falling back to basic markup.",
  );
};

function Avatar({
  className,
  ...props
}: React.ComponentProps<typeof RadixAvatar.Root>) {
  if (!hasRadixAvatar) {
    warnMissingRadixAvatar();
    const { asChild, ...rest } = props as any;
    return (
      <div
        data-slot="avatar-fallback"
        className={cn(
          "relative flex size-10 shrink-0 overflow-hidden rounded-full",
          className,
        )}
        {...rest}
      />
    );
  }
  return (
    <RadixAvatar.Root
      data-slot="avatar"
      className={cn(
        "relative flex size-10 shrink-0 overflow-hidden rounded-full",
        className,
      )}
      {...props}
    />
  );
}

function AvatarImage({
  className,
  src,
  ...props
}: React.ComponentProps<typeof RadixAvatar.Image>) {
  const driveFileId = React.useMemo(() => parseDriveAvatarFileId(src), [src]);
  const [resolvedSrc, setResolvedSrc] = React.useState<string | undefined>(
    driveFileId ? undefined : (typeof src === "string" ? src : undefined),
  );
  // One re-mint per file: a cached URL can be dead before its stated expiry,
  // and without this the image loads, errors, and every later render is
  // handed the same corpse for the rest of the 10-minute TTL.
  const retriedRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    if (!driveFileId) {
      setResolvedSrc(typeof src === "string" ? src : undefined);
      return () => {
        cancelled = true;
      };
    }

    setResolvedSrc(undefined);

    resolveDriveAvatarAccessUrl(driveFileId)
      .then((accessUrl) => {
        if (!cancelled) {
          setResolvedSrc(accessUrl);
        }
      })
      .catch((error) => {
        logEdgeTransportIssueOnce("ui/avatar", error);
        if (!cancelled) {
          setResolvedSrc(undefined);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [driveFileId, src]);

  const handleLoadingStatusChange = React.useCallback((status: string) => {
    (props as any).onLoadingStatusChange?.(status);
    if (status !== 'error' || !driveFileId) return;
    if (retriedRef.current === driveFileId) return;
    retriedRef.current = driveFileId;
    invalidateDriveAvatarAccessUrl(driveFileId);
    resolveDriveAvatarAccessUrl(driveFileId)
      .then(setResolvedSrc)
      .catch(() => setResolvedSrc(undefined));
  }, [driveFileId, props]);

  // Create a wrapper to add crossOrigin support
  const imgProps = React.useMemo(() => {
    if (resolvedSrc) {
      return {
        ...props,
        src: resolvedSrc,
        crossOrigin: 'anonymous' as const,
      };
    }
    return { ...props, src: undefined };
  }, [resolvedSrc, props]);

  // While a drive-file URL is still resolving, return null so Radix Avatar
  // stays in fallback state rather than settling into an error state from src=undefined.
  if (driveFileId && resolvedSrc === undefined) {
    return null;
  }

  if (!hasRadixAvatar) {
    warnMissingRadixAvatar();
    const {
      asChild,
      onLoadingStatusChange,
      ...rest
    } = imgProps as any;
    return (
      <img
        data-slot="avatar-image-fallback"
        className={cn("aspect-square size-full", className)}
        {...rest}
      />
    );
  }
  return (
    <RadixAvatar.Image
      data-slot="avatar-image"
      className={cn("aspect-square size-full", className)}
      {...imgProps}
      onLoadingStatusChange={handleLoadingStatusChange}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof RadixAvatar.Fallback>) {
  if (!hasRadixAvatar) {
    warnMissingRadixAvatar();
    const { asChild, ...rest } = props as any;
    return (
      <span
        data-slot="avatar-fallback-fallback"
        className={cn(
          "bg-muted flex size-full items-center justify-center rounded-full",
          className,
        )}
        {...rest}
      />
    );
  }
  return (
    <RadixAvatar.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "bg-muted flex size-full items-center justify-center rounded-full",
        className,
      )}
      {...props}
    />
  );
}

export { Avatar, AvatarImage, AvatarFallback };
